import { GraduationCap, Hammer, Award, HeartHandshake, ArrowDown } from "lucide-react";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import CycleIcon from "@/components/CycleIcon";

const stages = [
  {
    num: "01",
    icon: GraduationCap,
    title: "Student",
    verb: "learns",
    text: "It starts with opportunity. High school students step onto real job sites and see how homes are built.",
  },
  {
    num: "02",
    icon: Hammer,
    title: "Apprentice",
    verb: "skills",
    text: "Students learn from builders and tradespeople, use real tools, and start building real skills.",
  },
  {
    num: "03",
    icon: Award,
    title: "Journeyman",
    verb: "earns",
    text: "Skills become a career. Students grow into skilled professionals who keep mastering their trade.",
  },
  {
    num: "04",
    icon: HeartHandshake,
    title: "Philanthropist",
    verb: "gives",
    text: "Successful professionals give their time, knowledge, and resources to the next generation.",
  },
];

export default function Blueprint() {
  return (
    <main data-testid="blueprint-page">
      {/* HERO — graphic front and center */}
      <section className="bg-white text-navy blueprint-grid-light pt-40 pb-24 md:pt-48 md:pb-32" data-testid="blueprint-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-head text-xs font-bold uppercase tracking-[0.25em] text-brand-dark mb-6" data-testid="blueprint-overline">The Framework</p>
              <h1 className="font-display uppercase text-navy text-5xl md:text-7xl leading-[0.95] mb-8" data-testid="blueprint-headline">
                Blueprint for<br /><span className="text-brand-dark">Success</span>
              </h1>
              <p className="text-slate-600 text-base md:text-lg leading-relaxed max-w-md" data-testid="blueprint-intro">
                The Blueprint for Success is the framework behind BuilderEDU — a simple cycle that
                turns students into skilled professionals, and professionals into the mentors of
                the next generation.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-7">
            <img
              src="/assets/blueprint-for-success.png"
              alt="The BuilderEDU Blueprint for Success cycle: Student learns, Apprentice builds skills, Journeyman keeps learning, Philanthropist gives back"
              className="w-full h-auto"
              data-testid="blueprint-image"
            />
          </Reveal>
        </div>
      </section>

      <Marquee />

      {/* THE CYCLE */}
      <section className="bg-white blueprint-grid-light py-24 md:py-32" data-testid="cycle-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-end justify-between gap-8 mb-14 md:mb-20">
              <div>
                <p className="font-head text-xs font-bold uppercase tracking-[0.25em] text-brand-dark mb-4">How the Cycle Works</p>
                <h2 className="font-display uppercase text-navy text-4xl md:text-6xl leading-none" data-testid="cycle-heading">
                  One Cycle.<br />Every Generation.
                </h2>
              </div>
              <span className="hidden md:block font-head text-xs uppercase tracking-[0.3em] text-navy/30 pb-2">04 Stages</span>
            </div>
          </Reveal>

          <div>
            {stages.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.06}>
                <div
                  className={`group relative grid md:grid-cols-12 gap-4 md:gap-8 items-center border-t border-navy/10 py-10 md:py-14 ${i === 1 ? "md:pl-16" : i === 2 ? "md:pl-32" : i === 3 ? "md:pl-48" : ""}`}
                  data-testid={`cycle-stage-${s.num}`}
                >
                  {i > 0 && (
                    <span
                      className={`absolute top-0 -translate-y-1/2 left-5 ${i === 1 ? "md:left-[5.25rem]" : i === 2 ? "md:left-[9.25rem]" : "md:left-[13.25rem]"}`}
                      data-testid={`cycle-arrow-${s.num}`}
                      aria-hidden="true"
                    >
                      <ArrowDown size={20} className="text-brand-dark/60" />
                    </span>
                  )}
                  <div className="md:col-span-3 flex items-center gap-5">
                    <span className="font-display text-5xl md:text-7xl leading-none text-navy/10 group-hover:text-brand transition-colors duration-500" data-testid={`cycle-stage-${s.num}-number`}>
                      {s.num}
                    </span>
                    <s.icon size={34} className="text-navy/30 group-hover:text-brand-dark group-hover:-translate-y-1 transition-all duration-300" />
                  </div>
                  <div className="md:col-span-4">
                    <h3 className="font-display uppercase text-2xl md:text-4xl text-navy" data-testid={`cycle-stage-${s.num}-title`}>{s.title}</h3>
                    <span className="inline-block mt-2 font-head text-[11px] font-bold uppercase tracking-[0.2em] text-brand-dark bg-brand/15 px-3 py-1" data-testid={`cycle-stage-${s.num}-verb`}>
                      {s.verb}
                    </span>
                  </div>
                  <p className="md:col-span-5 text-slate-600 leading-relaxed max-w-md" data-testid={`cycle-stage-${s.num}-text`}>
                    {s.text}
                  </p>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-navy/10" />
          </div>

          <Reveal delay={0.1}>
            <div className="mt-20 md:mt-24 flex items-center gap-6 text-navy" data-testid="cycle-repeat-note">
              <CycleIcon className="w-14 h-14 md:w-16 md:h-16 shrink-0" />
              <p className="font-head font-bold text-2xl md:text-3xl leading-snug">
                Then the cycle begins again —
                <span className="block pl-8 md:pl-12 text-brand-dark md:whitespace-nowrap text-xl md:text-2xl mt-1">New students. New skills. New careers. New giving.</span>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="bg-white text-navy blueprint-grid-light py-24 md:py-32 border-t border-navy/5" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-head text-xs font-bold uppercase tracking-[0.25em] text-brand-dark mb-6">Why It Matters</p>
            <p className="font-head font-semibold text-navy text-2xl md:text-4xl leading-snug max-w-4xl" data-testid="why-statement">
              BuilderEDU isn't a one-time field trip. It's a cycle —
              <span className="text-brand-dark"> students learn, skills grow, careers launch, and professionals give back</span> —
              so every generation builds the next.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
